import type { ReactNode } from 'react';
import { CircleAlert, Database, Plug } from 'lucide-react';
import { cn } from './utils/cn';

export type ProviderBadgeVariant = 'demo' | 'live' | 'inline';

export interface ProviderBadgeProps {
  /**
   * `demo`   — full-width banner: DEMO mode, mock data
   * `live`   — small "live" pill (TickStar EU / ANZ / SEA)
   * `inline` — tiny inline marker next to a heading
   */
  variant?: ProviderBadgeVariant;
  /** Displayed label, e.g. "DEMO · mock TickStar" or "TickStar EU · live". */
  label: ReactNode;
  /** Optional secondary line, e.g. environment region or quota hint. */
  sub?: ReactNode;
  className?: string;
}

/**
 * Provider-status badge for the admin console.
 *
 * Used on the /tickstar page and AboutPage to surface whether the platform
 * is talking to the real TickStar APIs or running off the in-memory mock.
 * In live mode the badge is amber (low key); in demo mode it's bold orange
 * so the operator never confuses simulated data for production traffic.
 *
 * The customer portal does NOT render this badge — per [[solara-tickstar-swap]]
 * decision (white-label = Solara keeps a single brand face).
 */
export function ProviderBadge({
  variant = 'demo',
  label,
  sub,
  className,
}: ProviderBadgeProps) {
  if (variant === 'inline') {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
          'bg-amber-50 text-amber-800 border border-amber-200',
          className,
        )}
      >
        <CircleAlert className="h-3 w-3" />
        {label}
      </span>
    );
  }
  if (variant === 'live') {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium',
          'bg-emerald-50 text-emerald-700 border border-emerald-200',
          className,
        )}
      >
        <Plug className="h-3.5 w-3.5" />
        <span>{label}</span>
        {sub ? <span className="text-emerald-700/70">· {sub}</span> : null}
      </span>
    );
  }
  // demo
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-xl border-2 border-amber-400 bg-amber-50 px-4 py-3 text-sm text-amber-900',
        className,
      )}
    >
      <Database className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
      <div className="flex-1">
        <div className="font-semibold">{label}</div>
        {sub ? <div className="text-xs text-amber-800/80 mt-0.5">{sub}</div> : null}
      </div>
    </div>
  );
}
